import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { importControlAPI } from '../services/importControlAPI';
import FileErrorModal from './FileErrorModal';

const UploadModal = ({
  visible,
  onHide,
  onUploadComplete,
  dataKey, // Recebe o objeto { month: '2026-03', emp: {...} }
  monthList // Recebe a lista de meses para o Dropdown
}) => {
  const toast = useRef(null);
  const fileUploadRef = useRef(null); // <--- 1. Criamos a ref para o FileUpload

  // Estados baseados no rowData vindo do Dashboard
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Sincroniza os estados quando o rowData mudar (ao abrir o modal)
  useEffect(() => {
    if (dataKey) {
      setSelectedCompany(dataKey.rowData);
      setSelectedMonth(dataKey.monthKey);
    }
  }, [dataKey]);

  // Função auxiliar para resetar totalmente o arquivo selecionado
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear(); // <--- 2. Limpa o estado interno do componente FileUpload
    }
  };

  const handleUpload = async () => {
    if (!selectedCompany || !selectedMonth || !selectedFile) {
      toast.current.show({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos obrigatórios'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Filtrar history por month = selectedMonth e pegar id
      const importId = selectedCompany.history?.find(h => h.month === selectedMonth)?.id;
      const formData = new FormData();
      formData.append('files', selectedFile);
      formData.append('enterprise_id', selectedCompany.id);
      formData.append('enterprise_name', selectedCompany.enterprise_name || selectedCompany.nome);
      formData.append('layout_name', selectedCompany.layout_name || selectedCompany.layout_sigla);
      formData.append('month', selectedMonth);
      formData.append('import_ids[]', importId);

      // Simulação de progresso
      const interval = setInterval(() => {
        setUploadProgress(prev => (prev >= 90 ? prev : prev + 10));
      }, 150);

      await importControlAPI.upload(formData);

      clearInterval(interval);
      setUploadProgress(100);

      toast.current.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Arquivo enviado com sucesso!'
      });

      if (onUploadComplete) onUploadComplete();

      setTimeout(() => {
        onHide();
        handleRemoveFile(); // <--- 3. Reseta o arquivo e o componente ao fechar por sucesso
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      // Captura o erro retornado pela API Node.js
      const isPdfImageError = error?.response?.data?.code === 'PDF_IMAGE_ONLY' ||
        error?.message?.includes('imagem');

      if (isPdfImageError) {
        // Exibe o Modal do PDF Imagem se a API acusar esse problema
        setErrorMessage(error?.response?.data?.message || error.message);
        setShowErrorModal(true);
      } else {
        // Mantém a notificação padrão para outros erros
        toast.current.show({
          severity: 'error',
          summary: 'Erro',
          detail: error.message || 'Falha ao enviar arquivo'
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const footerContent = (
    <div className="flex gap-2">
      <Button label="Cancelar" icon="pi pi-times" onClick={onHide} className="p-button-text" disabled={uploading} />
      <Button label="Receber" icon="pi pi-download" onClick={handleUpload} loading={uploading} disabled={!selectedFile} />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <FileErrorModal
        visible={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        errorDetails={errorMessage}
        tutorialUrl="https://youtube.com"
      />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Arquivo de Importação"
        modal
        style={{ width: '500px' }}
        footer={footerContent}
        closable={!uploading}
      >
        <div className="flex flex-column gap-4 mt-2">

          {/* Cliente / Empresa (Input Desabilitado) */}
          <div className="flex flex-column gap-2">
            <label className="font-semibold">Cliente / Layout</label>
            <InputText
              value={selectedCompany ? selectedCompany.layout_name : ''}
              readOnly
              className="w-full bg-gray-100"
            />
            {selectedCompany && (
              <div className="flex align-items-center gap-2 p-2">
                <i className="pi pi-check-circle text-green-500"></i>
                <span className="font-medium text-sm text-700">Layout Selecionado:</span>
                <Badge
                  value={selectedCompany.layout_id + " | " + selectedCompany.layout_sigla}
                  severity="info"
                />
              </div>
            )}
          </div>

          {/* Mês de Referência (Dropdown Selecionado) */}
          <div className="flex flex-column gap-2">
            <label htmlFor="month" className="font-semibold">Mês de Referência *</label>
            <Dropdown
              id="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.value)}
              options={monthList} // Usa os meses gerados no Dashboard
              placeholder="Selecione o mês"
              className="w-full"
            />
          </div>

          {/* Upload de Arquivo */}
          <div className="flex flex-column gap-2">
            <label className="font-semibold">Arquivo *</label>
            <div
              className="border-2 border-dashed border-round p-4 text-center surface-card"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) setSelectedFile(file);
              }}
            >
              <FileUpload
                ref={fileUploadRef} // <--- 4. Atribuímos a ref aqui
                mode="basic"
                chooseLabel="Selecionar Arquivo"
                className="w-full"
                auto
                customUpload
                uploadHandler={(e) => setSelectedFile(e.files[0])}
                accept=".pdf,.xlsx,.xls,.csv"
              />
              {selectedFile && (
                <div className="mt-3 p-2 surface-100 border-round flex align-items-center justify-content-between">
                  <div className="flex align-items-center">
                    <i className="pi pi-file mr-2 text-primary"></i>
                    <span className="font-medium truncate" style={{ maxWidth: '250px' }}>{selectedFile.name}</span>
                  </div>
                  {/* 5. Alterado para chamar a função que limpa o state e o FileUpload */}
                  <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-rounded" onClick={handleRemoveFile} />
                </div>
              )}
            </div>
          </div>

          {uploading && (
            <div className="flex flex-column gap-2">
              <span className="text-sm">Processando...</span>
              <ProgressBar value={uploadProgress} showValue={false} className="h-2" />
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default UploadModal;